import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  CheckCircle, Clock, AlertTriangle, MapPin, Calendar,
  Timer, ChevronRight, Briefcase, Bell,
} from 'lucide-react';
import { formatLongDate, formatTimeRange, formatTime12h, tzAbbrev } from '../../utils/timeFormat';
import { toast } from 'sonner';
import AddToCalendarButton from '../AddToCalendarButton';

// Iter-156 P0 — Shift Confirmation Widget
//
// Required fields per user spec:
//   • Event/venue name
//   • Date           (MM-DD-YYYY)
//   • Time           (12-hour AM/PM start–end)
//   • Venue address
//   • Role
//   • Confirm button (action)
//
// Behaviour:
//   • Auto-pops a Dialog when the talent first lands on the dashboard
//     IF they have any pending confirmations — once-per-session via
//     sessionStorage so it doesn't nag.
//   • Confirm action calls /shift-confirmation/confirm and immediately
//     removes the booking from the list. Once the list is empty, the
//     widget hides itself and the modal closes.
//   • Banner remains visible (prominent amber Card) regardless of the
//     modal so talent can still confirm later if they dismiss the popup.
//
const SESSION_KEY = 'ievntur_shift_confirm_modal_seen';

const ShiftConfirmationWidget = () => {
  const navigate = useNavigate();
  const [pendingBookings, setPendingBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);
  // Iter-156f — keep just-confirmed bookings on screen briefly so the
  // talent can see + use the "Add to Calendar" button before the row
  // disappears. Auto-cleared 30s after confirm.
  const [recentlyConfirmed, setRecentlyConfirmed] = useState([]);

  useEffect(() => {
    fetchPendingConfirmations();
  }, []);

  const fetchPendingConfirmations = async () => {
    try {
      const res = await api.get('/shift-confirmation/pending');
      const list = res.data.bookings || [];
      setPendingBookings(list);
      // Auto-open modal once per session if there are pending shifts
      if (list.length > 0) {
        try {
          if (!sessionStorage.getItem(SESSION_KEY)) {
            setPopupOpen(true);
            sessionStorage.setItem(SESSION_KEY, '1');
          }
        } catch { /* sessionStorage unavailable */ }
      }
    } catch (error) {
      console.error('Failed to load pending confirmations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (bookingId, e) => {
    if (e?.stopPropagation) e.stopPropagation();
    setConfirming(bookingId);
    try {
      await api.post('/shift-confirmation/confirm', { booking_id: bookingId });
      toast.success('Shift confirmed! Don\'t forget to add it to your calendar.');
      // Move the row from pending → recentlyConfirmed
      setPendingBookings((prev) => {
        const justConfirmed = prev.find((b) => b.booking_id === bookingId);
        if (justConfirmed) {
          setRecentlyConfirmed((rc) => [...rc, justConfirmed]);
          // Auto-clear after 30s so the widget eventually goes empty.
          setTimeout(() => {
            setRecentlyConfirmed((rc) => rc.filter((b) => b.booking_id !== bookingId));
          }, 30000);
        }
        const next = prev.filter((b) => b.booking_id !== bookingId);
        if (next.length === 0) setPopupOpen(false);
        return next;
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm shift');
    } finally {
      setConfirming(null);
    }
  };

  const formatTimeRemaining = (hours, isExpired) => {
    if (isExpired) return 'EXPIRED';
    if (hours === null || hours === undefined) return 'Check deadline';
    if (hours <= 0) return 'Expired';
    if (hours < 1) return `${Math.round(hours * 60)}m left`;
    if (hours < 24) return `${Math.round(hours)}h left`;
    const days = Math.floor(hours / 24);
    const remainingHours = Math.round(hours % 24);
    return `${days}d ${remainingHours}h left`;
  };

  if (loading) {
    return (
      <Card className="mb-6 bg-white border-gray-200 shadow-sm" data-testid="confirmation-widget-loading">
        <CardContent className="p-6 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-cyan-500"></div>
        </CardContent>
      </Card>
    );
  }

  if (pendingBookings.length === 0 && recentlyConfirmed.length === 0) {
    return null; // Don't show widget if no pending OR recently-confirmed
  }

  // ── Reusable booking row (used in both banner + modal) ──────────────
  const BookingRow = ({ booking, prominent }) => (
    <div
      className={`p-3 rounded-lg border bg-white ${prominent ? 'border-amber-300' : (booking.is_urgent ? 'border-red-300' : 'border-amber-200')}`}
      data-testid={`pending-shift-${booking.booking_id}`}
    >
      {/* Row 1 — Event/venue + URGENT badge */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-gray-900 truncate" data-testid={`pending-shift-venue-${booking.booking_id}`}>
            {booking.venue_name || 'Upcoming Shift'}
          </p>
          <p className="text-xs text-gray-600 flex items-center gap-1 mt-0.5">
            <Briefcase className="w-3 h-3 flex-shrink-0" />
            <span className="truncate" data-testid={`pending-shift-role-${booking.booking_id}`}>{booking.role_name}</span>
          </p>
        </div>
        {booking.is_urgent && (
          <Badge variant="destructive" className="text-xs flex-shrink-0">
            <Timer className="w-3 h-3 mr-1" />
            URGENT
          </Badge>
        )}
      </div>

      {/* Row 2 — Date / Time / Address */}
      <div className="text-xs text-gray-700 space-y-1 mb-3">
        <div className="flex items-center gap-1.5">
          <Calendar className="w-3 h-3 text-gray-500 flex-shrink-0" />
          <span data-testid={`pending-shift-date-${booking.booking_id}`}>{formatLongDate(booking.shift_date)}</span>
          {(booking.start_time || booking.end_time) && (
            <>
              <span className="text-gray-400">·</span>
              <Clock className="w-3 h-3 text-gray-500 flex-shrink-0" />
              <span data-testid={`pending-shift-time-${booking.booking_id}`}>
                {booking.end_time ? formatTimeRange(booking.start_time, booking.end_time) : formatTime12h(booking.start_time)}{' '}{tzAbbrev(booking.timezone, booking.state, booking.shift_date)}
              </span>
            </>
          )}
        </div>
        {(booking.venue_address || booking.venue_name) && (
          <div className="flex items-start gap-1.5">
            <MapPin className="w-3 h-3 text-gray-500 flex-shrink-0 mt-0.5" />
            <span className="text-gray-600" data-testid={`pending-shift-address-${booking.booking_id}`}>
              {booking.venue_address || booking.venue_name}
            </span>
          </div>
        )}
        <div className={`flex items-center gap-1 font-medium ${booking.is_urgent ? 'text-red-600' : 'text-amber-700'}`}>
          <Timer className="w-3 h-3 flex-shrink-0" />
          {formatTimeRemaining(booking.hours_remaining, booking.is_expired)}
        </div>
      </div>

      {/* Row 3 — Confirm button */}
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          onClick={(e) => handleConfirm(booking.booking_id, e)}
          disabled={confirming === booking.booking_id}
          className={`flex-1 ${booking.is_urgent ? 'bg-red-500 hover:bg-red-600' : 'bg-cyan-500 hover:bg-cyan-600'} text-white`}
          data-testid={`confirm-btn-${booking.booking_id}`}
        >
          {confirming === booking.booking_id ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
          ) : (
            <>
              <CheckCircle className="w-4 h-4 mr-1.5" />
              Confirm
            </>
          )}
        </Button>
        {/* Iter-156f — Add to Calendar always available so booked talent
            can plan logistics even before they tap Confirm. The calendar
            event uses the event timezone, not the device's. */}
        <AddToCalendarButton bookingId={booking.booking_id} label="Calendar" />
        <Button
          size="sm"
          variant="ghost"
          onClick={() => navigate('/talent/bookings')}
          data-testid={`pending-shift-details-${booking.booking_id}`}
        >
          Details <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );

  return (
    <>
      {/* Iter-156f — Just-confirmed shifts get a green banner with the
          "Add to Calendar" CTA so the talent can capture the event in
          their personal calendar before the row auto-clears (~30s). */}
      {recentlyConfirmed.length > 0 && (
        <Card
          className="mb-4 bg-emerald-50 border-emerald-300 shadow-sm"
          data-testid="shift-confirmation-recently-confirmed"
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-emerald-900">
              <CheckCircle className="w-5 h-5" />
              Confirmed — add to your calendar?
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-2 space-y-3">
            {recentlyConfirmed.map((booking) => (
              <div
                key={booking.booking_id}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-white rounded-lg border border-emerald-200"
                data-testid={`recently-confirmed-${booking.booking_id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-gray-900 truncate">
                    {booking.venue_name || 'Confirmed shift'}
                  </p>
                  <p className="text-xs text-gray-600 mt-0.5">
                    {booking.role_name}
                    {' • '}
                    {formatLongDate(booking.shift_date)}
                    {booking.start_time ? ` at ${formatTime12h(booking.start_time)} ${tzAbbrev(booking.timezone, booking.state, booking.shift_date)}` : ''}
                  </p>
                </div>
                <AddToCalendarButton
                  bookingId={booking.booking_id}
                  variant="default"
                  className="bg-emerald-500 hover:bg-emerald-600 text-white"
                  label="Add to Calendar"
                />
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Prominent always-on banner */}
      {pendingBookings.length > 0 && (
      <Card
        className="mb-6 bg-amber-50 border-amber-300 shadow-md"
        data-testid="shift-confirmation-widget"
      >
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2 text-amber-900">
            <Bell className="w-5 h-5" />
            You are booked — please confirm
            <Badge variant="destructive" className="ml-2" data-testid="pending-confirmations-count">
              {pendingBookings.length}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-2">
          <p className="text-sm text-amber-800 mb-4 font-medium">
            You are booked for an upcoming shift. Please confirm now —
            unconfirmed shifts may be auto-released.
          </p>

          <div className="space-y-3">
            {pendingBookings.slice(0, 3).map((booking) => (
              <BookingRow key={booking.booking_id} booking={booking} prominent={false} />
            ))}
          </div>

          {pendingBookings.length > 3 && (
            <Button
              variant="link"
              className="w-full mt-3 text-amber-700"
              onClick={() => navigate('/talent/bookings')}
              data-testid="view-all-pending-confirmations"
            >
              View all {pendingBookings.length} pending confirmations →
            </Button>
          )}
        </CardContent>
      </Card>
      )}

      {/* Auto-popup on first dashboard load */}
      <Dialog open={popupOpen} onOpenChange={setPopupOpen}>
        <DialogContent className="max-w-md" data-testid="shift-confirmation-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-900">
              <AlertTriangle className="w-5 h-5" />
              Confirm your upcoming shift
            </DialogTitle>
            <DialogDescription>
              You are booked for an upcoming shift. Please confirm now —
              unconfirmed shifts may be auto-released.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto">
            {pendingBookings.slice(0, 3).map((booking) => (
              <BookingRow key={booking.booking_id} booking={booking} prominent />
            ))}
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setPopupOpen(false)}
              data-testid="shift-confirmation-modal-dismiss"
            >
              Remind me later
            </Button>
            {pendingBookings.length > 3 && (
              <Button
                variant="outline"
                onClick={() => { setPopupOpen(false); navigate('/talent/bookings'); }}
              >
                View all {pendingBookings.length}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ShiftConfirmationWidget;
